/**
 * Room Scenes Carousel - Main JavaScript Module
 * 
 * Handles:
 * - Image filtering based on filename patterns
 * - Variant-specific matching
 * - Carousel functionality with auto-rotation
 * - Responsive touch/swipe support
 * - Accessibility features
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  // ============================================================
  // CONFIGURATION
  // ============================================================
  
  const CONFIG = {
    autoPlayInterval: 5000, // 5 seconds
    transitionDuration: 500, // 500ms for slide transition
    debugMode: true, // Enable detailed logging
    imageExtensions: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg']
  };

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================
  
  /**
   * Normalize a string for comparison
   * - Convert to lowercase
   * - Treat hyphens, underscores, and spaces as equivalent
   * - Remove file extensions
   * 
   * @param {string} str - The string to normalize
   * @returns {string} Normalized string
   */
  function normalizeString(str) {
    if (!str || typeof str !== 'string') return '';
    
    // Remove file extension
    let normalized = str.split('.').slice(0, -1).join('.') || str;
    
    // Convert to lowercase
    normalized = normalized.toLowerCase();
    
    // Replace hyphens, underscores, and spaces with a common separator (space)
    // This allows us to compare "bliss-11.64" with "bliss_11.64" or "bliss 11.64"
    normalized = normalized.replace(/[-_\s]+/g, ' ');
    
    // Remove extra spaces
    normalized = normalized.trim().replace(/\s+/g, ' ');
    
    return normalized;
  }

  /**
   * Log messages if debug mode is enabled
   * 
   * @param {string} type - Log type (log, warn, error, info)
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   */
  function log(type, message, data) {
    if (!CONFIG.debugMode) return;
    
    const prefix = '[Room Scenes]';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    
    if (data !== undefined) {
      console[type](`${prefix} [${timestamp}] ${message}`, data);
    } else {
      console[type](`${prefix} [${timestamp}] ${message}`);
    }
  }

  /**
   * Extract the base filename from a full URL or path
   * 
   * @param {string} url - Full image URL
   * @returns {string} Base filename without path
   */
  function getFilename(url) {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Check if a filename matches a room scene pattern
   * 
   * @param {string} filename - The filename to check
   * @param {string} pattern - The pattern to match (rs, roomscene, etc.)
   * @param {string} variant - Optional variant identifier
   * @returns {boolean} True if matches
   */
  function matchesPattern(filename, pattern, variant = null) {
    const normalizedFilename = normalizeString(filename);
    const normalizedPattern = normalizeString(pattern);
    
    if (variant) {
      const normalizedVariant = normalizeString(variant);
      // Check if filename contains both the pattern and variant
      return normalizedFilename.includes(normalizedPattern) && 
             normalizedFilename.includes(normalizedVariant);
    } else {
      // For exact matches, check if filename starts with pattern
      return normalizedFilename.startsWith(normalizedPattern);
    }
  }

  /**
   * Check if filename starts with a prefix (case-insensitive, separator-normalized)
   * 
   * @param {string} filename - The filename to check
   * @param {string} prefix - The prefix to match
   * @returns {boolean} True if starts with prefix
   */
  function startsWithPrefix(filename, prefix) {
    const normalized = normalizeString(filename);
    const normalizedPrefix = normalizeString(prefix);
    return normalized.startsWith(normalizedPrefix);
  }

  /**
   * Check if filename ends with a suffix (case-insensitive, separator-normalized)
   * 
   * @param {string} filename - The filename to check
   * @param {string} suffix - The suffix to match
   * @returns {boolean} True if ends with suffix
   */
  function endsWithSuffix(filename, suffix) {
    const normalized = normalizeString(filename);
    const normalizedSuffix = normalizeString(suffix);
    return normalized.endsWith(normalizedSuffix);
  }

  /**
   * Natural sort comparison function
   * Handles numeric sorting (e.g., RoomScene_1, RoomScene_2, RoomScene_10)
   * 
   * @param {string} a - First string
   * @param {string} b - Second string
   * @returns {number} Comparison result
   */
  function naturalSort(a, b) {
    const regex = /(\d+)|(\D+)/g;
    const aParts = a.match(regex);
    const bParts = b.match(regex);
    
    if (!aParts || !bParts) return a.localeCompare(b);
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = aParts[i] || '';
      const bPart = bParts[i] || '';
      
      const aNum = parseInt(aPart, 10);
      const bNum = parseInt(bPart, 10);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum !== bNum) return aNum - bNum;
      } else {
        const comparison = aPart.localeCompare(bPart);
        if (comparison !== 0) return comparison;
      }
    }
    
    return 0;
  }

  /**
   * Check if a variant token matches the filename
   * Uses normalized comparison
   * 
   * @param {string} filename - The filename to check
   * @param {string} token - The variant token (part of variant title or option value)
   * @returns {boolean} True if matches
   */
  function filenameContainsToken(filename, token) {
    if (!token) return false;
    const normalized = normalizeString(filename);
    const normalizedToken = normalizeString(token);
    return normalized.includes(normalizedToken);
  }

  // ============================================================
  // IMAGE FILTERING & MATCHING
  // ============================================================
  
  /**
   * Filter room scene images based on matching rules
   * 
   * Priority order:
   * 1. Variant-specific matches (when variant is provided)
   * 2. Generic matches (RS or roomscene exact)
   * 3. Legacy matches (*Room or *RoomScene)
   * 4. ALT-text fallback (images with "room scene" in ALT)
   * 5. Final fallback (filename or ALT contains "room")
   * 
   * @param {Array} images - Array of product images
   * @param {Object} variant - Selected variant object (optional)
   * @param {Array} variantOptions - Array of variant option values (optional)
   * @returns {Array} Filtered and sorted room scene images
   */
  function filterRoomScenes(images, variant = null, variantOptions = []) {
    log('info', '=== Starting Room Scene Filtering ===');
    log('info', `Total images to process: ${images.length}`);
    log('info', `Variant:`, variant);
    log('info', `Variant options:`, variantOptions);
    
    const variantSpecific = [];
    const generic = [];
    const legacy = [];
    const altText = [];
    const roomFallback = [];
    
    images.forEach((image, index) => {
      const filename = getFilename(image.filename);
      const normalizedFilename = normalizeString(filename);
      
      log('log', `\n--- Processing image ${index + 1}/${images.length} ---`);
      log('log', `Filename: ${filename}`);
      log('log', `Normalized: ${normalizedFilename}`);
      log('log', `ALT text: ${image.alt || 'none'}`);
      
      // Check if it's an image (not video)
      const extension = filename.split('.').pop().toLowerCase();
      if (!CONFIG.imageExtensions.includes(extension)) {
        log('warn', `Skipping non-image file: ${filename}`);
        return;
      }
      
      // Skip if ALT text contains video indicators
      if (image.alt && (
        image.alt.toLowerCase().includes('youtube') ||
        image.alt.toLowerCase().includes('vimeo') ||
        image.alt.toLowerCase().includes('.mp4')
      )) {
        log('warn', `Skipping video: ${filename}`);
        return;
      }
      
      let matchType = null;
      let matchDetails = '';
      
      // ========== TIER 1: PRIMARY PATTERNS (RS- or roomscene-) ==========
      const hasPrimaryPrefix = startsWithPrefix(filename, 'rs-') || 
                               startsWithPrefix(filename, 'rs_') ||
                               startsWithPrefix(filename, 'rs ') ||
                               startsWithPrefix(filename, 'roomscene-') ||
                               startsWithPrefix(filename, 'roomscene_') ||
                               startsWithPrefix(filename, 'roomscene ');
      
      if (hasPrimaryPrefix) {
        log('info', '✓ Matched primary prefix pattern');
        
        // Check if this is variant-specific
        if (variant) {
          // Try matching with variant title first
          const variantTitle = variant.title;
          const variantTitleParts = variantTitle.split('/').map(p => p.trim());
          
          let isVariantMatch = false;
          
          // Check full variant title
          if (filenameContainsToken(filename, variantTitle)) {
            matchType = 'variant-specific-primary';
            matchDetails = `Matched variant title: ${variantTitle}`;
            isVariantMatch = true;
          }
          
          // Check variant title parts (e.g., "28.22 / Ash Brown" -> ["28.22", "Ash Brown"])
          if (!isVariantMatch) {
            for (const part of variantTitleParts) {
              if (filenameContainsToken(filename, part)) {
                matchType = 'variant-specific-primary';
                matchDetails = `Matched variant title part: ${part}`;
                isVariantMatch = true;
                break;
              }
            }
          }
          
          // Try matching with individual option values
          if (!isVariantMatch && variantOptions && variantOptions.length > 0) {
            for (const option of variantOptions) {
              if (filenameContainsToken(filename, option)) {
                matchType = 'variant-specific-primary';
                matchDetails = `Matched option value: ${option}`;
                isVariantMatch = true;
                break;
              }
            }
          }
          
          if (isVariantMatch) {
            log('info', `✓✓ VARIANT-SPECIFIC MATCH (Primary): ${matchDetails}`);
            variantSpecific.push({ ...image, matchType, matchDetails });
            return;
          }
        }
        
        // Check if this is a generic match (exact RS or roomscene)
        const normalizedBase = normalizedFilename.split('.')[0]; // Remove extension
        if (normalizedBase === 'rs' || normalizedBase === 'roomscene') {
          matchType = 'generic-primary';
          matchDetails = 'Generic room scene (exact match)';
          log('info', `✓✓ GENERIC MATCH (Primary): ${matchDetails}`);
          generic.push({ ...image, matchType, matchDetails });
          return;
        }
        
        // Otherwise, it's a generic primary pattern match
        matchType = 'generic-primary';
        matchDetails = 'Primary pattern but no variant match';
        log('info', `✓ Generic primary pattern: ${matchDetails}`);
        generic.push({ ...image, matchType, matchDetails });
        return;
      }
      
      // ========== TIER 2: LEGACY PATTERNS (*Room or *RoomScene) ==========
      const hasLegacySuffix = endsWithSuffix(filename, 'room') || 
                              endsWithSuffix(filename, 'roomscene');
      
      if (hasLegacySuffix) {
        matchType = 'legacy';
        matchDetails = 'Legacy suffix pattern (*Room or *RoomScene)';
        log('info', `✓ LEGACY MATCH: ${matchDetails}`);
        legacy.push({ ...image, matchType, matchDetails });
        return;
      }
      
      // ========== TIER 3: ALT-TEXT FALLBACK ==========
      if (image.alt && image.alt.toLowerCase().includes('room scene')) {
        matchType = 'alt-text';
        matchDetails = 'ALT text contains "room scene"';
        log('info', `✓ ALT-TEXT MATCH: ${matchDetails}`);
        altText.push({ ...image, matchType, matchDetails });
        return;
      }
      
      // ========== TIER 4: FINAL FALLBACK (contains "room") ==========
      const filenameContainsRoom = normalizedFilename.includes('room');
      const altContainsRoom = image.alt && image.alt.toLowerCase().includes('room');
      
      if (filenameContainsRoom || altContainsRoom) {
        matchType = 'room-fallback';
        if (filenameContainsRoom && altContainsRoom) {
          matchDetails = 'Filename and ALT text contain "room"';
        } else if (filenameContainsRoom) {
          matchDetails = 'Filename contains "room"';
        } else {
          matchDetails = 'ALT text contains "room"';
        }
        log('info', `✓ ROOM FALLBACK MATCH: ${matchDetails}`);
        roomFallback.push({ ...image, matchType, matchDetails });
        return;
      }
      
      log('log', '✗ No match');
    });
    
    // ========== COMPILE RESULTS IN PRIORITY ORDER ==========
    let results = [];
    
    if (variantSpecific.length > 0) {
      log('info', `\n📊 Found ${variantSpecific.length} variant-specific matches`);
      results = results.concat(variantSpecific);
    }
    
    // Only add generic if no variant-specific matches or if no variant is selected
    if (variantSpecific.length === 0 || !variant) {
      if (generic.length > 0) {
        log('info', `\n📊 Found ${generic.length} generic matches`);
        results = results.concat(generic);
      }
    }
    
    // Only add legacy if no primary matches
    if (results.length === 0 && legacy.length > 0) {
      log('info', `\n📊 Found ${legacy.length} legacy matches`);
      results = results.concat(legacy);
    }
    
    // Only add ALT-text if no other matches
    if (results.length === 0 && altText.length > 0) {
      log('info', `\n📊 Found ${altText.length} ALT-text matches`);
      results = results.concat(altText);
    }
    
    // Only add room fallback if no other matches
    if (results.length === 0 && roomFallback.length > 0) {
      log('info', `\n📊 Found ${roomFallback.length} room fallback matches`);
      results = results.concat(roomFallback);
    }
    
    // ========== SORT RESULTS ==========
    results.sort((a, b) => {
      const filenameA = getFilename(a.filename);
      const filenameB = getFilename(b.filename);
      return naturalSort(filenameA, filenameB);
    });
    
    // ========== REMOVE DUPLICATES ==========
    const seen = new Set();
    results = results.filter(image => {
      if (seen.has(image.id)) {
        log('warn', `Removing duplicate image: ${getFilename(image.filename)}`);
        return false;
      }
      seen.add(image.id);
      return true;
    });
    
    log('info', `\n✅ Final result: ${results.length} room scene images`);
    if (results.length > 0) {
      log('info', 'Matched images:', results.map(img => ({
        filename: getFilename(img.filename),
        matchType: img.matchType,
        details: img.matchDetails
      })));
    }
    
    return results;
  }

  // ============================================================
  // LIGHTBOX CLASS
  // ============================================================
  
  class RoomScenesLightbox {
    constructor(images) {
      this.images = images;
      this.currentIndex = 0;
      this.lightbox = null;
      this.img = null;
      this.counter = null;
      
      this.createLightbox();
    }
    
    createLightbox() {
      // Create lightbox container
      this.lightbox = document.createElement('div');
      this.lightbox.className = 'room-scenes-lightbox';
      if (this.images.length === 1) {
        this.lightbox.classList.add('single-image');
      }
      this.lightbox.setAttribute('role', 'dialog');
      this.lightbox.setAttribute('aria-modal', 'true');
      this.lightbox.setAttribute('aria-label', 'Room scene image viewer');
      
      // Create content wrapper
      const content = document.createElement('div');
      content.className = 'room-scenes-lightbox-content';
      
      // Create image
      this.img = document.createElement('img');
      this.img.alt = '';
      content.appendChild(this.img);
      
      // Create close button
      const closeBtn = document.createElement('button');
      closeBtn.className = 'room-scenes-lightbox-close';
      closeBtn.setAttribute('aria-label', 'Close lightbox');
      closeBtn.innerHTML = '&times;';
      closeBtn.addEventListener('click', () => this.close());
      content.appendChild(closeBtn);
      
      // Create navigation (only if multiple images)
      if (this.images.length > 1) {
        // Previous button
        const prevBtn = document.createElement('button');
        prevBtn.className = 'room-scenes-lightbox-prev';
        prevBtn.setAttribute('aria-label', 'Previous image');
        prevBtn.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        prevBtn.addEventListener('click', () => this.prev());
        content.appendChild(prevBtn);
        
        // Next button
        const nextBtn = document.createElement('button');
        nextBtn.className = 'room-scenes-lightbox-next';
        nextBtn.setAttribute('aria-label', 'Next image');
        nextBtn.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        nextBtn.addEventListener('click', () => this.next());
        content.appendChild(nextBtn);
        
        // Counter
        this.counter = document.createElement('div');
        this.counter.className = 'room-scenes-lightbox-counter';
        content.appendChild(this.counter);
      }
      
      this.lightbox.appendChild(content);
      document.body.appendChild(this.lightbox);
      
      // Event listeners
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) {
          this.close();
        }
      });
      
      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!this.lightbox.classList.contains('active')) return;
        
        if (e.key === 'Escape') {
          this.close();
        } else if (e.key === 'ArrowLeft') {
          this.prev();
        } else if (e.key === 'ArrowRight') {
          this.next();
        }
      });
      
      log('info', '✅ Lightbox created');
    }
    
    open(index = 0) {
      this.currentIndex = index;
      this.updateImage();
      this.lightbox.classList.add('active');
      document.body.style.overflow = 'hidden'; // Prevent scrolling
      log('info', `🔍 Lightbox opened at index ${index}`);
    }
    
    close() {
      this.lightbox.classList.remove('active');
      document.body.style.overflow = ''; // Restore scrolling
      log('info', '🔍 Lightbox closed');
    }
    
    updateImage() {
      const image = this.images[this.currentIndex];
      this.img.src = image.srcLarge || image.src;
      this.img.alt = image.alt || `Room scene ${this.currentIndex + 1}`;
      
      if (this.counter) {
        this.counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
      }
    }
    
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.updateImage();
      log('log', `➡️ Lightbox next: ${this.currentIndex + 1}/${this.images.length}`);
    }
    
    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.updateImage();
      log('log', `⬅️ Lightbox prev: ${this.currentIndex + 1}/${this.images.length}`);
    }
    
    destroy() {
      if (this.lightbox && this.lightbox.parentNode) {
        this.lightbox.parentNode.removeChild(this.lightbox);
      }
    }
  }

  // ============================================================
  // CAROUSEL CLASS
  // ============================================================
  
  class RoomScenesCarousel {
    constructor(productId, productData) {
      this.productId = productId;
      this.productData = productData;
      this.container = document.getElementById('room-scenes-carousel');
      this.wrapper = document.getElementById('room-scenes-wrapper');
      this.slider = null;
      this.slides = [];
      this.currentIndex = 0;
      this.autoPlayTimer = null;
      this.isPlaying = false;
      this.lightbox = null;
      this.roomScenes = [];
      
      log('info', `\n🎬 Initializing carousel for product ${productId}`);
      
      this.init();
    }
    
    init() {
      // Get current variant if any
      const selectedVariant = this.getCurrentVariant();
      const variantOptions = selectedVariant ? selectedVariant.options : [];
      
      // Filter room scenes
      const roomScenes = filterRoomScenes(
        this.productData.images,
        selectedVariant,
        variantOptions
      );
      
      if (roomScenes.length === 0) {
        log('warn', 'No room scenes found - hiding carousel');
        this.hide();
        return;
      }
      
      // Render carousel
      this.render(roomScenes);
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Show carousel
      this.show();
      
      // Start auto-play if multiple slides
      if (roomScenes.length > 1) {
        this.startAutoPlay();
      }
    }
    
    getCurrentVariant() {
      const selectedId = this.productData.selectedVariantId;
      if (!selectedId) return null;
      
      return this.productData.variants.find(v => v.id === selectedId) || null;
    }
    
    render(roomScenes) {
      log('info', `🎨 Rendering ${roomScenes.length} room scene(s)`);
      
      if (!this.container) {
        log('error', 'Carousel container not found');
        return;
      }
      
      // Store room scenes for lightbox
      this.roomScenes = roomScenes;
      
      this.slider = this.container.querySelector('.room-scenes-slider');
      const dotsContainer = this.container.querySelector('.room-scenes-dots');
      
      if (!this.slider || !dotsContainer) {
        log('error', 'Slider or dots container not found');
        return;
      }
      
      // Clear existing content
      this.slider.innerHTML = '';
      dotsContainer.innerHTML = '';
      this.slides = [];
      
      // Create or update lightbox
      if (this.lightbox) {
        this.lightbox.destroy();
      }
      this.lightbox = new RoomScenesLightbox(roomScenes);
      
      // Add single-image class if only one image
      if (roomScenes.length === 1) {
        this.container.classList.add('single-image');
      } else {
        this.container.classList.remove('single-image');
      }
      
      // Create slides
      roomScenes.forEach((scene, index) => {
        // Create slide
        const slide = document.createElement('div');
        slide.className = 'room-scene-slide';
        if (index === 0) slide.classList.add('active');
        slide.setAttribute('data-index', index);
        
        const img = document.createElement('img');
        img.src = scene.srcMedium || scene.src;
        img.srcset = `
          ${scene.srcSmall || scene.src} 600w,
          ${scene.srcMedium || scene.src} 1024w,
          ${scene.srcLarge || scene.src} 2048w
        `;
        img.sizes = '(max-width: 768px) 600px, 1024px';
        img.alt = scene.alt || `Room scene ${index + 1}`;
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.setAttribute('tabindex', '0');
        img.setAttribute('role', 'button');
        img.setAttribute('aria-label', `View ${scene.alt || 'room scene'} in full size`);
        
        // Add click handler to open lightbox
        img.addEventListener('click', () => {
          this.lightbox.open(index);
        });
        
        // Add keyboard handler for accessibility
        img.addEventListener('keypress', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.lightbox.open(index);
          }
        });
        
        slide.appendChild(img);
        this.slider.appendChild(slide);
        this.slides.push(slide);
        
        // Create dot
        if (roomScenes.length > 1) {
          const dot = document.createElement('button');
          dot.className = 'room-scene-dot';
          if (index === 0) dot.classList.add('active');
          dot.setAttribute('aria-label', `View room scene ${index + 1}`);
          dot.setAttribute('data-index', index);
          dot.addEventListener('click', () => this.goToSlide(index));
          dotsContainer.appendChild(dot);
        }
      });
      
      log('info', '✅ Carousel rendered successfully');
    }
    
    setupEventListeners() {
      // Navigation buttons
      const prevBtn = this.container.querySelector('.room-scenes-prev');
      const nextBtn = this.container.querySelector('.room-scenes-next');
      
      if (prevBtn) {
        prevBtn.addEventListener('click', () => this.prev());
      }
      
      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.next());
      }
      
      // Pause on hover
      this.container.addEventListener('mouseenter', () => this.pauseAutoPlay());
      this.container.addEventListener('mouseleave', () => this.resumeAutoPlay());
      
      // Pause on focus
      this.container.addEventListener('focusin', () => this.pauseAutoPlay());
      this.container.addEventListener('focusout', () => this.resumeAutoPlay());
      
      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prev();
        } else if (e.key === 'ArrowRight') {
          this.next();
        }
      });
      
      // Variant change detection
      this.setupVariantChangeListener();
      
      log('info', '✅ Event listeners setup complete');
    }
    
    setupVariantChangeListener() {
      log('info', '🔗 Setting up variant change listeners...');
      const self = this;
      
      // Listen for variant select dropdown changes
      const variantSelect = document.querySelector(`#product-select-${this.productId}`);
      
      if (variantSelect) {
        log('info', `✓ Found main variant select: #product-select-${this.productId}`);
        variantSelect.addEventListener('change', (e) => {
          const variantId = parseInt(e.target.value, 10);
          log('info', `🔄 Variant changed to ID: ${variantId}`);
          this.productData.selectedVariantId = variantId;
          this.handleVariantChange(variantId);
        });
      } else {
        log('warn', `✗ Main variant select not found: #product-select-${this.productId}`);
      }
      
      // Function to attach listeners to single-option-selectors
      const attachSingleOptionListeners = () => {
        const optionSelectors = document.querySelectorAll('.single-option-selector');
        log('info', `Found ${optionSelectors.length} single-option-selector(s) for direct listener attachment`);
        
        optionSelectors.forEach((selector, index) => {
          // Check if we already attached our listener
          if (selector.hasAttribute('data-room-scenes-listener')) {
            return; // Skip if already attached
          }
          
          // Mark this selector as having our listener
          selector.setAttribute('data-room-scenes-listener', 'true');
          
          selector.addEventListener('change', function(e) {
            log('info', `🎯 Single-option-selector #${index} changed detected (direct listener)`);
            
            // Small delay to ensure all selectors have updated
            setTimeout(() => {
              // Get all selected options to construct variant title
              const selectedOptions = [];
              document.querySelectorAll('.single-option-selector').forEach(s => {
                selectedOptions.push(s.value);
              });
              
              const variantTitle = selectedOptions.join(' / ');
              log('info', `🔄 Variant changed via dropdown to: ${variantTitle}`);
              
              // Find matching variant ID
              const matchedVariant = self.productData.variants.find(v => 
                v.title.toLowerCase() === variantTitle.toLowerCase()
              );
              
              if (matchedVariant) {
                self.productData.selectedVariantId = matchedVariant.id;
                self.handleVariantChange(matchedVariant.id, variantTitle);
              } else {
                log('warn', `No matching variant found for title: ${variantTitle}`);
              }
            }, 50);
          }, false); // Use bubble phase to work with existing handlers
        });
      };
      
      // Try immediately
      attachSingleOptionListeners();
      
      // Try again after short delay (for Shopify OptionSelectors initialization)
      setTimeout(() => {
        log('info', '🔄 Retrying single-option-selector listener attachment after delay...');
        attachSingleOptionListeners();
      }, 1000);
      
      // Try again after longer delay as backup
      setTimeout(() => {
        log('info', '🔄 Final retry for single-option-selector listener attachment...');
        attachSingleOptionListeners();
      }, 3000);
      
      // Also use event delegation as fallback
      document.addEventListener('change', function(e) {
        if (e.target && e.target.classList && e.target.classList.contains('single-option-selector')) {
          log('info', '🎯 Single-option-selector changed detected (delegation listener)');
          
          setTimeout(() => {
            const selectedOptions = [];
            document.querySelectorAll('.single-option-selector').forEach(s => {
              selectedOptions.push(s.value);
            });
            
            const variantTitle = selectedOptions.join(' / ');
            log('info', `🔄 Variant changed via dropdown (delegation) to: ${variantTitle}`);
            
            const matchedVariant = self.productData.variants.find(v => 
              v.title.toLowerCase() === variantTitle.toLowerCase()
            );
            
            if (matchedVariant) {
              self.productData.selectedVariantId = matchedVariant.id;
              self.handleVariantChange(matchedVariant.id, variantTitle);
            } else {
              log('warn', `No matching variant found for title: ${variantTitle}`);
            }
          }, 50);
        }
      }, true); // Use capture phase
      
      // Also listen for custom variant change events (if theme uses them)
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant && e.detail.variant.id) {
          log('info', `🔄 Variant changed (custom event) to ID: ${e.detail.variant.id}`);
          this.productData.selectedVariantId = e.detail.variant.id;
          this.handleVariantChange(e.detail.variant.id);
        }
      });
      
      log('info', '✅ Variant change listeners setup complete');
    }
    
    handleVariantChange(variantId, variantTitle = null) {
      // Find the variant object
      const variant = this.productData.variants.find(v => v.id === variantId);
      if (!variant) {
        log('warn', `Variant ${variantId} not found in product data`);
        this.refresh();
        return;
      }
      
      log('info', `🎯 Handling variant change for: ${variant.title}`);
      
      // Update product thumbnail carousel to show selected variant's thumbnail
      this.updateProductThumbnailCarousel(variant);
      
      // Check if we have room scenes already loaded
      if (this.roomScenes && this.roomScenes.length > 0) {
        // Try to find a matching room scene for this variant
        const matchingSceneIndex = this.findMatchingRoomScene(variant);
        
        if (matchingSceneIndex >= 0) {
          log('info', `✅ Found matching room scene at index ${matchingSceneIndex}`);
          // Navigate to the matching room scene
          this.goToSlide(matchingSceneIndex);
          // Stop autoplay to indicate this is the variant's room scene
          this.stopAutoPlay();
          log('info', '⏸️ Autoplay stopped to show variant-specific room scene');
        } else {
          log('info', 'No specific room scene match found for this variant');
          // Refresh the carousel to refilter room scenes
          this.refresh();
        }
      } else {
        // No room scenes loaded yet, do a full refresh
        this.refresh();
      }
    }
    
    updateProductThumbnailCarousel(variant) {
      // Find matching thumbnail in the product thumbnail carousel
      const variantTitle = variant.title;
      log('info', `🖼️ Updating product thumbnail carousel for variant: ${variantTitle}`);
      
      // Try to find thumbnail by variant title
      const thumbnails = document.querySelectorAll('#thumbnail-carousel .slides img');
      if (thumbnails.length === 0) {
        log('warn', 'No product thumbnails found');
        return;
      }
      
      let matchingThumbnail = null;
      let thumbnailIndex = -1;
      
      thumbnails.forEach((thumb, index) => {
        const thumbVariantTitle = thumb.getAttribute('data-variant-title');
        if (thumbVariantTitle && thumbVariantTitle.toLowerCase() === variantTitle.toLowerCase()) {
          matchingThumbnail = thumb;
          thumbnailIndex = index;
          log('info', `  ✓ Found matching thumbnail at index ${index}: ${thumbVariantTitle}`);
        }
      });
      
      if (matchingThumbnail && thumbnailIndex >= 0) {
        // Scroll the thumbnail carousel to show this thumbnail (centered)
        const thumbnailCarousel = document.querySelector('#thumbnail-carousel');
        if (thumbnailCarousel && thumbnailCarousel.flexslider) {
          // Calculate center offset (carousel shows 4 items at a time)
          const visibleItems = 4;
          const centerOffset = Math.floor(visibleItems / 2);
          const targetIndex = Math.max(0, thumbnailIndex - centerOffset);
          
          log('info', `📍 Scrolling product thumbnail carousel to center thumbnail at index ${thumbnailIndex}, target position: ${targetIndex}`);
          thumbnailCarousel.flexslider.flexAnimate(targetIndex);
        }
      } else {
        log('warn', `No matching thumbnail found for variant: ${variantTitle}`);
      }
    }
    
    findMatchingRoomScene(variant) {
      // Search through current room scenes to find one matching this variant
      const variantTitle = variant.title;
      const variantTitleParts = variantTitle.split('/').map(p => p.trim());
      const variantOptions = variant.options || [];
      
      log('info', `🔍 Searching for room scene matching variant: ${variantTitle}`);
      log('info', `Variant title parts:`, variantTitleParts);
      log('info', `Variant options:`, variantOptions);
      
      for (let i = 0; i < this.roomScenes.length; i++) {
        const scene = this.roomScenes[i];
        const filename = getFilename(scene.filename);
        const normalizedFilename = normalizeString(filename);
        
        log('log', `  Checking scene ${i}: ${filename}`);
        
        // Check if filename contains the full variant title
        if (filenameContainsToken(filename, variantTitle)) {
          log('info', `  ✓ Match found: filename contains full variant title`);
          return i;
        }
        
        // Check if filename contains any variant title part
        for (const part of variantTitleParts) {
          if (filenameContainsToken(filename, part)) {
            log('info', `  ✓ Match found: filename contains variant title part "${part}"`);
            return i;
          }
        }
        
        // Check if filename contains any variant option value
        for (const option of variantOptions) {
          if (filenameContainsToken(filename, option)) {
            log('info', `  ✓ Match found: filename contains option "${option}"`);
            return i;
          }
        }
        
        // Check ALT text as fallback
        if (scene.alt) {
          const altLower = scene.alt.toLowerCase();
          const variantTitleLower = variantTitle.toLowerCase();
          
          if (altLower.includes(variantTitleLower)) {
            log('info', `  ✓ Match found: ALT text contains variant title`);
            return i;
          }
          
          // Check ALT text for variant parts
          for (const part of variantTitleParts) {
            if (altLower.includes(part.toLowerCase())) {
              log('info', `  ✓ Match found: ALT text contains variant part "${part}"`);
              return i;
            }
          }
        }
      }
      
      log('info', '  ✗ No matching room scene found');
      return -1; // No match found
    }
    
    refresh() {
      log('info', '🔄 Refreshing carousel...');
      this.stopAutoPlay();
      this.currentIndex = 0;
      this.init();
    }
    
    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      
      log('log', `➡️ Going to slide ${index + 1}`);
      
      // Update active slide
      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('active');
        } else {
          slide.classList.remove('active');
        }
      });
      
      // Update active dot
      const dots = this.container.querySelectorAll('.room-scene-dot');
      dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
      
      // Update slider transform
      const offset = -index * 100;
      this.slider.style.transform = `translateX(${offset}%)`;
      
      this.currentIndex = index;
    }
    
    next() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    }
    
    prev() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.goToSlide(prevIndex);
    }
    
    startAutoPlay() {
      if (this.slides.length <= 1) return;
      
      this.isPlaying = true;
      this.autoPlayTimer = setInterval(() => {
        if (this.isPlaying) {
          this.next();
        }
      }, CONFIG.autoPlayInterval);
      
      log('info', '▶️ Auto-play started');
    }
    
    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
      this.isPlaying = false;
      log('info', '⏹️ Auto-play stopped');
    }
    
    pauseAutoPlay() {
      this.isPlaying = false;
      log('log', '⏸️ Auto-play paused');
    }
    
    resumeAutoPlay() {
      if (this.autoPlayTimer && this.slides.length > 1) {
        this.isPlaying = true;
        log('log', '▶️ Auto-play resumed');
      }
    }
    
    show() {
      if (this.container) {
        this.container.style.display = 'block';
        this.container.setAttribute('role', 'region');
        this.container.setAttribute('aria-label', 'Room scenes');
        log('info', '👁️ Carousel shown');
      }
    }
    
    hide() {
      if (this.container) {
        this.container.style.display = 'none';
        this.container.setAttribute('aria-hidden', 'true');
        log('info', '🙈 Carousel hidden');
      }
    }
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================
  
  /**
   * Initialize room scenes carousel when DOM is ready
   */
  function initRoomScenes() {
    log('info', '\n🚀 Initializing Room Scenes Module');
    
    // Check if RoomScenesData exists
    if (typeof window.RoomScenesData === 'undefined') {
      log('warn', 'No RoomScenesData found - carousel disabled');
      return;
    }
    
    // Find all products on the page
    const productKeys = Object.keys(window.RoomScenesData);
    
    if (productKeys.length === 0) {
      log('warn', 'No product data found in RoomScenesData');
      return;
    }
    
    // Initialize carousel for each product
    productKeys.forEach(key => {
      const productData = window.RoomScenesData[key];
      const productId = productData.productId;
      
      log('info', `\n📦 Processing product: ${productData.productTitle} (ID: ${productId})`);
      
      // Create carousel instance
      new RoomScenesCarousel(productId, productData);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRoomScenes);
  } else {
    initRoomScenes();
  }

  // Expose utilities for external use (debugging)
  window.RoomScenesCarousel = {
    version: '1.0.0',
    utils: {
      normalizeString,
      getFilename,
      naturalSort
    },
    config: CONFIG
  };

  log('info', '✅ Room Scenes module loaded successfully\n');

})();

